import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Navbar.css';

interface NavLink {
  href?: string;
  to?: string;
  label: string;
  isButton?: boolean;
}

interface NavbarProps {
  logo?: {
    src?: string;
    text: string;
  };
  links: NavLink[];
  overlay?: boolean;
  overlayUntil?: string;
}

const findOverlayUntil = (selector?: string) => {
  if (selector) {
    return document.querySelector(selector);
  }

  return (
    document.querySelector('#services, .services-section') ||
    document.querySelector('.consulting-hero')?.nextElementSibling ||
    document.querySelector('footer')
  );
};

const Navbar: React.FC<NavbarProps> = ({ 
  logo = { text: 'Orca Labs' }, 
  links,
  overlay = false,
  overlayUntil,
}) => {
  const navRef = useRef<HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!overlay) return undefined;

    const updateOverlay = () => {
      const nav = navRef.current;
      const until = findOverlayUntil(overlayUntil);
      if (!nav || !until) {
        setScrolled(false);
        return;
      }

      setScrolled(nav.getBoundingClientRect().bottom >= until.getBoundingClientRect().top);
    };

    updateOverlay();
    window.addEventListener('scroll', updateOverlay, { passive: true });
    window.addEventListener('resize', updateOverlay);
    return () => {
      window.removeEventListener('scroll', updateOverlay);
      window.removeEventListener('resize', updateOverlay);
    };
  }, [overlay, overlayUntil]);

  const className = [
    'consulting-navbar',
    overlay ? 'consulting-navbar--overlay' : '',
    overlay && scrolled ? 'is-scrolled' : '',
  ].filter(Boolean).join(' ');

  return (
    <nav ref={navRef} className={className}>
      <div className="nav-container">
        <Link to="/" className="logo">
          {logo.src && (
            <img src={logo.src} alt={logo.text} height="40" width="40" />
          )}
          <span className="logo-text">{logo.text}</span>
        </Link>
        <div className="nav-links">
          {links.map((link, index) => {
            if (link.isButton) {
              const Button = link.to ? (
                <Link to={link.to} key={index}>
                  <button className="btn-primary">{link.label}</button>
                </Link>
              ) : (
                <a href={link.href} key={index}>
                  <button className="btn-primary">{link.label}</button>
                </a>
              );
              return Button;
            }
            
            return link.to ? (
              <Link to={link.to} key={index}>{link.label}</Link>
            ) : (
              <a href={link.href} key={index}>{link.label}</a>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;

